
import { Component, Prop, Ref, Vue } from "vue-property-decorator"
import { TemplateDetails } from "@/types"
import { TemplateTreeNode } from "@/types/treeNode"
import { FieldOverrideLabels, SectionOverrideLabels } from "@/types/inheritanceOverrides"

@Component({
	components: {
		"tree-templates": () => import("./TreeTemplates.vue")
	}
})
export default class TreeTemplates extends Vue {
	@Ref() childNodes!: TreeTemplates[]

	@Prop({ type: Object, required: true })
	private item!: TemplateTreeNode

	@Prop({ type: Number, default: 0 })
	private level!: number

    @Prop({ type: Boolean, default: false })
    private showOnlyOverrides!: boolean

	@Prop({ type: Object, required: false, default: () => ({}) })
	private affected?: { fieldIds?: Set<string>; sectionIds?: Set<string> }

	@Prop({ type: String, default: null })
	private readonly emptyMessage!: string | null

	@Prop({ type: Boolean, default: false })
	private readonly collapseByDefault!: Boolean

	@Prop({ type: Boolean, default: false })
	private readonly collapseChildrenByDefault!: Boolean

	private expanded: boolean = !this.collapseByDefault
	private fieldsExpanded: boolean = !this.collapseChildrenByDefault
	private sectionsExpanded: boolean = !this.collapseChildrenByDefault

	private get filteredFields () {
		const nodeFields = this.item.node.fields || []

		// If affected fields are specified, filter to only those (takes priority)
		if (this.affected?.fieldIds && this.affected.fieldIds.size > 0) {
			return nodeFields.filter(f => {
				const id = f.field.id
				return id !== undefined && this.affected!.fieldIds!.has(id)
			})
		}

		// Otherwise, filter by overrides if showOnlyOverrides is true
		if (!this.showOnlyOverrides) return nodeFields

		// Filter to only show fields that have overrides at THIS node
		return nodeFields.filter(f => {
			const id = f.field.id
			if (!id) return false
			const fieldOverrides = this.item.node.inheritanceOverrides?.fields?.[id]
			return fieldOverrides && Array.isArray(fieldOverrides) && fieldOverrides.length > 0
		})
	}

	private get filteredSections () {
		const nodeSections = this.item.node.sections || []

		// If affected sections are specified, filter to only those (takes priority)
		if (this.affected?.sectionIds && this.affected.sectionIds.size > 0) {
			return nodeSections.filter(s => this.affected!.sectionIds!.has(s))
		}

		// Otherwise, filter by overrides if showOnlyOverrides is true
		if (!this.showOnlyOverrides) return nodeSections

		// Filter to only show sections that have overrides at THIS node
		return nodeSections.filter(s => {
			const sectionOverrides = this.item.node.inheritanceOverrides?.sections?.[s]
			return sectionOverrides && Array.isArray(sectionOverrides) && sectionOverrides.length > 0
		})
	}

	private get overrideCountLabel (): string | null {
		const count = this.countOverrides(this.item.node)
		if (count === 0) return null
		return `(${count} override${count === 1 ? "" : "s"})`
	}

	private get hasVisibleContent (): boolean {
		return this.filteredFields.length > 0 || this.filteredSections.length > 0
	}

	public expandAll (): void {
		this.expanded = true
		this.fieldsExpanded = true
		this.sectionsExpanded = true

		if (this.childNodes) {
			this.childNodes.forEach(child => {
				child.expandAll()
			})
		}
	}

	public collapseAll (childrenOnly: boolean = false): void {
		if (!childrenOnly) {
			this.expanded = false
		}

		this.fieldsExpanded = false
		this.sectionsExpanded = false

		if (this.childNodes) {
			this.childNodes.forEach(child => {
				child.collapseAll()
			})
		}
	}

	private getFieldLine (fieldId: string) {
		const fieldOverrides = this.item.node.inheritanceOverrides?.fields?.[fieldId] ?? []
		return FieldOverrideLabels.map(aspect => ({
			label: aspect.label,
			isOverridden: fieldOverrides.includes(aspect.key)
		}))
	}

	private getSectionLine (sectionId: string) {
		const sectionOverrides = this.item.node.inheritanceOverrides?.sections?.[sectionId] ?? []
		return SectionOverrideLabels.map(aspect => ({
			label: aspect.label,
			isOverridden: sectionOverrides.includes(aspect.key)
		}))
	}

	private getSectionTitle (sectionId: string): string {
		const details = this.item.node.sectionDetails && this.item.node.sectionDetails[sectionId]
		return details && details.title ? details.title : sectionId
	}

	private isFieldInherited (field: any): boolean {
		return !!field.inheritedFrom
	}

	private isSectionInherited (sectionId: string): boolean {
		const details = this.item.node.sectionDetails && this.item.node.sectionDetails[sectionId]
		return !!(details && details.inheritedFrom)
	}

	private getSectionInheritedFromTitle (sectionId: string): string {
		const details = this.item.node.sectionDetails && this.item.node.sectionDetails[sectionId]
		return details && details.inheritedFromTitle ? details.inheritedFromTitle : ""
	}

	/**
     * Counts the total number of overridden aspects in a template.
     *
     * Sums all overridden properties found under:
     *   - template.inheritanceOverrides.fields
     *   - template.inheritanceOverrides.sections
     *
     * Each overridden property (e.g. RequiredOnRecord, RequiredOnCase, Permissions)
     * counts as one. Returns 0 if no overrides are defined.
     *
     * @param template - The template whose overrides to count.
     * @returns The total number of overridden aspects in the template.
     */
	private countOverrides (template: TemplateDetails): number {
		if (!template?.inheritanceOverrides) return 0

		let total = 0

		if (template.inheritanceOverrides.fields) {
			for (const fieldId in template.inheritanceOverrides.fields) {
				const fieldOverrides = template.inheritanceOverrides.fields[fieldId]
				if (fieldOverrides) {
					total += fieldOverrides.length
				}
			}
		}

		if (template.inheritanceOverrides.sections) {
			for (const sectionId in template.inheritanceOverrides.sections) {
				const sectionOverrides = template.inheritanceOverrides.sections[sectionId]
				if (sectionOverrides) {
					total += sectionOverrides.length
				}
			}
		}

		return total
	}
}
